import Component from '@glimmer/component';
import { guidFor } from '@ember/object/internals';
import { Heading, Text } from 'tio-ui/components/typography';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// PageHero – gradient hero banner component
// ---------------------------------------------------------------------------

/**
 * Theme preset type for gradient backgrounds.
 * Each theme maps to a specific Tailwind gradient.
 */
export type PageHeroTheme =
  | 'violet'
  | 'emerald'
  | 'amber'
  | 'teal'
  | 'gray'
  | 'indigo'
  | 'sky'
  | 'blue'
  | 'rose'
  | 'red'
  | 'fuchsia';

/**
 * Corner rounding preset type.
 */
export type PageHeroRounded = 'lg' | 'xl' | '2xl' | '3xl';

/**
 * Decorative background pattern type.
 */
export type PageHeroDecoration = 'none' | 'circles' | 'dots' | 'confetti' | 'smiley';

export interface PageHeroSignature {
  Args: {
    /** Gradient theme preset (default: 'violet') */
    theme?: PageHeroTheme;
    /** Custom gradient class (overrides theme) */
    gradientClass?: string;
    /** Rounded corners (default: 'xl') */
    rounded?: PageHeroRounded;
    /** Decorative background pattern (default: 'none') */
    decoration?: PageHeroDecoration;
    /** Center-align icon, text, and badge blocks (block mode only; has no effect in convenience mode) */
    centered?: boolean;
    /** Convenience: title text (renders Heading in icon area) */
    title?: string;
    /** Convenience: subtitle text below the title */
    subtitle?: string;
    /** Convenience: Phosphor icon component to display beside the title */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Convenience: heading level for the title (default: 1) */
    headingLevel?: 1 | 2 | 3;
  };
  Blocks: {
    default: [];
    icon?: [];
    badge?: [];
    footer?: [];
    /** Optional stat display (right side, requires @title convenience mode) */
    stat?: [];
    /** Optional action buttons below subtitle (requires @title convenience mode) */
    actions?: [];
    /** Optional custom content below title area (requires @title convenience mode) */
    content?: [];
  };
  Element: HTMLDivElement;
}

/**
 * A gradient hero banner with optional named blocks for icon, badge, body,
 * and footer content. Supports preset gradient themes, decorative SVG
 * background patterns, and flexible rounding.
 *
 * **Convenience mode** — use `@title`/`@subtitle`/`@icon` args for HeroBanner-style layout:
 *
 * @example
 * ```gts
 * import PageHero from 'tio-reimagined/components/page-hero';
 * import { PhPiggyBank } from 'ember-phosphor-icons';
 *
 * <PageHero
 *   @icon={{PhPiggyBank}}
 *   @title="Section 127 Optimizer"
 *   @subtitle="Maximize your tax-free education benefit"
 *   @theme="emerald"
 * >
 *   <:stat>
 *     <div class="text-4xl font-bold">$5,250</div>
 *   </:stat>
 *   <:actions>
 *     <Button>Take Action</Button>
 *   </:actions>
 * </PageHero>
 * ```
 *
 * **Block mode** — use named blocks for structured layout:
 *
 * @example
 * ```gts
 * <PageHero @theme="emerald" @decoration="circles" @rounded="2xl">
 *   <:icon>
 *     <PhGraduationCap @size={{32}} />
 *     <Heading @level={{1}} class="text-white">Page Title</Heading>
 *   </:icon>
 *   <:default>
 *     <p>Hero body content goes here.</p>
 *   </:default>
 *   <:footer>
 *     <div class="grid grid-cols-3 gap-4">Stats go here</div>
 *   </:footer>
 * </PageHero>
 * ```
 */
export default class PageHero extends Component<PageHeroSignature> {
  get gradientClass() {
    if (this.args.gradientClass) return this.args.gradientClass;
    switch (this.args.theme) {
      case 'emerald':
        return 'bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800';
      case 'amber':
        return 'bg-gradient-to-r from-amber-500 to-orange-500';
      case 'teal':
        return 'bg-gradient-to-br from-teal-600 via-teal-700 to-cyan-800';
      case 'gray':
        return 'bg-gradient-to-r from-gray-600 to-slate-600';
      case 'indigo':
        return 'bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800';
      case 'sky':
        return 'bg-gradient-to-br from-sky-600 via-sky-700 to-blue-800';
      case 'blue':
        return 'bg-gradient-to-r from-blue-600 to-indigo-600';
      case 'rose':
        return 'bg-gradient-to-br from-rose-600 via-pink-700 to-fuchsia-800';
      case 'red':
        return 'bg-gradient-to-r from-red-600 to-rose-500';
      case 'fuchsia':
        return 'bg-gradient-to-br from-indigo-500 via-purple-500 to-fuchsia-500';
      default:
        return 'bg-gradient-to-r from-violet-600 to-purple-600';
    }
  }

  get roundedClass() {
    switch (this.args.rounded) {
      case 'lg':
        return 'rounded-lg';
      case 'xl':
        return 'rounded-xl';
      case '2xl':
        return 'rounded-2xl';
      case '3xl':
        return 'rounded-3xl';
      default:
        return 'rounded-xl';
    }
  }

  get showCircles() {
    return this.args.decoration === 'circles';
  }

  get showDots() {
    return this.args.decoration === 'dots';
  }

  get showConfetti() {
    return this.args.decoration === 'confetti';
  }

  get showSmiley() {
    return this.args.decoration === 'smiley';
  }

  get isCentered() {
    return this.args.centered === true;
  }

  /** Whether the component is in convenience mode (title arg provided). */
  get hasConvenienceTitle() {
    return !!this.args.title;
  }

  get headingLevel() {
    return this.args.headingLevel ?? 1;
  }

  /** Unique suffix for SVG pattern IDs to avoid collisions with multiple instances. */
  get patternId() {
    return `hero-${guidFor(this)}`;
  }

  <template>
    <div
      data-test-page-hero
      class="p-6 md:p-8 text-white mb-6 relative overflow-hidden
        {{this.gradientClass}}
        {{this.roundedClass}}"
      ...attributes
    >
      {{! Decorative background patterns }}
      {{#if this.showCircles}}
        <div class="absolute top-0 right-0 -mt-8 -mr-8 h-48 w-48 rounded-full bg-white/5"></div>
        <div class="absolute bottom-0 left-0 -mb-12 -ml-12 h-64 w-64 rounded-full bg-white/5"></div>
      {{else if this.showDots}}
        <div class="absolute inset-0 opacity-10" aria-hidden="true">
          <svg class="w-full h-full">
            <defs>
              <pattern
                id="{{this.patternId}}-dots"
                width="10"
                height="10"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="5" cy="5" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#{{this.patternId}}-dots)" />
          </svg>
        </div>
      {{else if this.showConfetti}}
        <div class="absolute inset-0 opacity-10" aria-hidden="true">
          <svg class="w-full h-full">
            <defs>
              <pattern
                id="{{this.patternId}}-confetti"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="5" cy="5" r="2" fill="white" />
                <circle cx="15" cy="15" r="1.5" fill="white" />
                <circle cx="10" cy="18" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#{{this.patternId}}-confetti)" />
          </svg>
        </div>
      {{else if this.showSmiley}}
        <div class="absolute inset-0 opacity-10" aria-hidden="true">
          <svg class="w-full h-full">
            <defs>
              <pattern
                id="{{this.patternId}}-smiley"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                {{! Face }}
                <circle cx="20" cy="20" r="12" fill="none" stroke="white" stroke-width="1.5" />
                {{! Eyes }}
                <circle cx="15" cy="17" r="1.5" fill="white" />
                <circle cx="25" cy="17" r="1.5" fill="white" />
                {{! Smile }}
                <path
                  d="M14 23 Q20 28 26 23"
                  fill="none"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#{{this.patternId}}-smiley)" />
          </svg>
        </div>
      {{/if}}

      {{! Content (z-10 keeps it above decorations) }}
      <div class="relative z-10">
        {{#if (has-block "icon")}}
          {{! Block mode: explicit icon block }}
          <div
            data-test-page-hero-icon
            class="flex items-center gap-3 mb-3 {{if this.isCentered 'justify-center'}}"
          >
            {{yield to="icon"}}
          </div>
          {{yield}}
        {{else if this.hasConvenienceTitle}}
          {{! Convenience mode: @title/@subtitle/@icon args with optional stat/actions }}
          <div class="flex justify-between items-start">
            <div class="flex-1">
              <div data-test-page-hero-icon class="flex items-center gap-2 mb-2">
                {{#if @icon}}
                  {{#let @icon as |Icon|}}
                    <Icon @size={{28}} />
                  {{/let}}
                {{/if}}
                <Heading
                  data-test-page-hero-title
                  @level={{this.headingLevel}}
                  class="text-white text-2xl"
                >
                  {{@title}}
                </Heading>
              </div>
              {{#if @subtitle}}
                <Text data-test-page-hero-subtitle class="text-white/80 text-lg max-w-2xl">
                  {{@subtitle}}
                </Text>
              {{/if}}
              {{#if (has-block "actions")}}
                <div data-test-page-hero-actions class="mt-4 flex items-center gap-3">
                  {{yield to="actions"}}
                </div>
              {{/if}}
            </div>
            {{#if (has-block "stat")}}
              <div
                data-test-page-hero-stat
                class="text-right bg-white/10 rounded-lg p-4 backdrop-blur-sm"
              >
                {{yield to="stat"}}
              </div>
            {{/if}}
          </div>
          {{yield}}
          {{#if (has-block "content")}}
            <div data-test-page-hero-content class="mt-6">
              {{yield to="content"}}
            </div>
          {{/if}}
        {{else}}
          {{! Minimal mode: just default block }}
          {{yield}}
        {{/if}}
        {{#if (has-block "badge")}}
          <div
            data-test-page-hero-badge
            class="flex flex-wrap gap-2 mt-4 {{if this.isCentered 'justify-center'}}"
          >
            {{yield to="badge"}}
          </div>
        {{/if}}
        {{#if (has-block "footer")}}
          <div data-test-page-hero-footer class="mt-6 pt-6 border-t border-white/10">
            {{yield to="footer"}}
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
